<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { offlineQueue } from '@/services/offline-queue'

const isOnline = ref(navigator.onLine)
const pendingCount = ref(0)
const isSyncing = ref(false)
const showDetails = ref(false)

const toast = useToast()

let unsubscribeQueue: (() => void) | null = null

function updateOnlineStatus(): void {
  isOnline.value = navigator.onLine
}

async function updatePendingCount(): Promise<void> {
  pendingCount.value = await offlineQueue.getPendingCount()
}

async function forceSync(): Promise<void> {
  if (isSyncing.value || !isOnline.value) return

  isSyncing.value = true
  try {
    await offlineQueue.forceSync()
    await updatePendingCount()
    if (pendingCount.value === 0) {
      toast.add({ severity: 'success', summary: 'Sincronizado', detail: 'Todas las escrituras pendientes se han sincronizado', life: 3000 })
    }
  } catch {
    toast.add({ severity: 'error', summary: 'Error de sincronización', detail: 'No se pudieron sincronizar todas las escrituras', life: 4000 })
  } finally {
    isSyncing.value = false
  }
}

function handleOnline(): void {
  updateOnlineStatus()
  updatePendingCount()
  toast.add({ severity: 'success', summary: 'Conexión restaurada', detail: 'Sincronizando escrituras pendientes...', life: 3000 })
}

function handleOffline(): void {
  updateOnlineStatus()
  updatePendingCount()
  toast.add({ severity: 'warn', summary: 'Sin conexión', detail: 'Las escrituras se guardarán localmente y se sincronizarán al volver la conexión', life: 5000 })
}

onMounted(() => {
  updateOnlineStatus()
  updatePendingCount()

  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)

  // Suscribirse a cambios en la cola offline
  unsubscribeQueue = offlineQueue.subscribe(updatePendingCount)

  // Procesar cola inicial si hay conexión
  if (navigator.onLine) {
    offlineQueue.processQueue()
  }
})

onUnmounted(() => {
  // Sin esto cada montaje (AppLayout, MobileShell, TabletShell, DesktopShell…)
  // dejaba dos listeners huérfanos acumulando toasts.
  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)
  unsubscribeQueue?.()
  unsubscribeQueue = null
})

// Observar cambios en isOnline para trigger sync
watch(isOnline, (online) => {
  if (online && pendingCount.value > 0) {
    offlineQueue.processQueue()
  }
})
</script>

<template>
  <div
    v-show="pendingCount > 0 || !isOnline"
    class="no-print fixed bottom-4 right-4 z-[var(--z-toast)] flex flex-col items-end gap-2"
  >
    <!-- Indicador principal -->
    <div
      class="flex items-center gap-2 px-3 py-2 rounded-xl shadow-lg backdrop-blur-sm border transition-all duration-300"
      :class="[
        isOnline 
          ? 'bg-green-500/90 dark:bg-green-500/90 text-white' 
          : 'bg-amber-500/90 dark:bg-amber-500/90 text-white',
        pendingCount > 0 ? 'animate-pulse' : '',
      ]"
      role="status"
      :aria-live="pendingCount > 0 ? 'assertive' : 'polite'"
    >
      <!--
        `pi-ban` y no `pi-wifi-off`: este último no existe en PrimeIcons 8, así
        que el indicador de «sin conexión» salía sin icono justo cuando más falta
        hace. Se comprueba en `primeIcons.test.ts`.
      -->
      <i
        class="text-lg"
        :class="isOnline ? 'pi pi-wifi' : 'pi pi-ban'"
      />
      <span class="text-sm font-medium">
        {{ isOnline ? 'En línea' : 'Sin conexión' }}
      </span>
      <span
        v-if="pendingCount > 0"
        class="flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-white/20"
      >
        {{ pendingCount }}
      </span>
    </div>

    <!-- Panel de detalles expandible -->
    <div
      v-show="showDetails"
      class="w-64 bg-white dark:bg-surface-900 rounded-xl shadow-xl border border-surface-200 dark:border-surface-700 p-3 animate-slide-up"
      role="region"
      aria-label="Estado de sincronización"
    >
      <div class="flex items-center justify-between mb-2">
        <h4 class="text-sm font-semibold text-surface-900 dark:text-surface-100">
          Sincronización
        </h4>
        <!--
          `min-h-11 min-w-11` (44px) y no `p-1`: un icono de 16px con 4px de
          padding deja un objetivo de 24px, por debajo del mínimo que piden las
          WCAG 2.5.5. La protección que hay en `DenseTable` solo cubre los botones
          de PrimeVue dentro de la tabla, así que un `<button>` nativo se
          queda fuera de ella.
        -->
        <button
          class="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-surface-400 transition-colors hover:bg-surface-100 hover:text-surface-600 dark:hover:bg-surface-800 dark:hover:text-surface-300"
          type="button"
          aria-label="Cerrar detalles"
          @click="showDetails = false"
        >
          <i class="pi pi-times" />
        </button>
      </div>
      
      <div class="space-y-2 text-sm">
        <div class="flex items-center justify-between">
          <span class="text-surface-600 dark:text-surface-400">Escrituras pendientes:</span>
          <span class="font-medium text-surface-900 dark:text-surface-100">{{ pendingCount }}</span>
        </div>
        
        <div class="flex items-center justify-between">
          <span class="text-surface-600 dark:text-surface-400">Estado:</span>
          <span :class="isOnline ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'">
            {{ isOnline ? 'Conectado' : 'Desconectado' }}
          </span>
        </div>
        
        <div
          v-if="isSyncing"
          class="flex items-center gap-2 text-primary-600 dark:text-primary-400"
        >
          <i class="pi pi-spin pi-spinner" />
          <span>Sincronizando...</span>
        </div>
      </div>
      
      <div class="flex gap-2 mt-3 pt-2 border-t border-surface-200 dark:border-surface-700">
        <!--
          `inline-flex items-center gap-2` en vez de contar con `mr-1` del icono: el
          margen a mano deja icono y texto alineados por la base, y con el
          `line-height: 1` de PrimeIcons el texto queda por debajo del centro del
          botón. `gap` es además lo que mantiene la separación al cambiar el
          tamaño de letra.
        -->
        <button
          v-if="pendingCount > 0 && isOnline && !isSyncing"
          type="button"
          class="btn-primary flex-1 text-xs py-1.5"
          @click="forceSync"
        >
          <i class="pi pi-sync" />
          Sincronizar ahora
        </button>
        <button
          v-else-if="isSyncing"
          type="button"
          class="btn-secondary flex-1 cursor-wait text-xs py-1.5"
          disabled
        >
          <i class="pi pi-spin pi-spinner" />
          Sincronizando...
        </button>
        <button
          v-else
          type="button"
          class="btn-secondary flex-1 text-xs py-1.5"
          disabled
        >
          {{ isOnline ? 'Todo sincronizado' : 'Esperando conexión...' }}
        </button>
      </div>
    </div>

    <!-- Botón toggle detalles -->
    <!--
      `min-h-11 min-w-11` en vez de `p-2`: el chevron mide 16px y con 8px de
      padding el objetivo se queda en 32px, por debajo de los 44px que piden las
      WCAG 2.5.5 para punteros gruesos. Este botón vive en la barra superior, que
      es justo donde más se usa con el dedo.
    -->
    <button
      type="button"
      class="flex min-h-11 min-w-11 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm transition-colors hover:bg-white/20 dark:bg-surface-900/10 dark:hover:bg-surface-800/20"
      :aria-expanded="showDetails"
      :aria-label="showDetails ? 'Ocultar detalles de sincronización' : 'Mostrar detalles de sincronización'"
      @click="showDetails = !showDetails"
    >
      <i
        :class="showDetails ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
        class="text-sm"
      />
    </button>
  </div>
</template>

<style scoped>
/* Animación de entrada */
@keyframes slide-up {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-slide-up {
  animation: slide-up 0.2s ease-out;
}

/* Reducir animaciones si el usuario lo prefiere */
@media (prefers-reduced-motion: reduce) {
  .animate-pulse,
  .animate-slide-up {
    animation: none;
  }
}
</style>